import { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import {
  Banknote, TrendingDown, Scale, Plus, X, Save, Trash2, Loader2,
  ArrowDownToLine, FileText, Pencil
} from "lucide-react";
import { toast } from "sonner";
import { confirmAction } from "@/lib/confirm";

/**
 * Page Encaissements — vision Entrées (factures auto + saisies manuelles).
 *
 *   • Auto :    db.invoices.payments       (paiements facture PitBoard)
 *   • Manuel :  db.incomes                 (clients utilisant un autre logiciel)
 *
 * Balance trésorerie toujours visible : Encaissements TTC − Dépenses TTC.
 */
const fmtEUR = (n) =>
  new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR" }).format(Number(n) || 0);

const METHOD_LABELS = {
  cb: "CB", virement: "Virement", cheque: "Chèque",
  especes: "Espèces", autre: "Autre",
};

const SOURCE_BADGE = {
  invoice: { label: "Facture PitBoard", color: "var(--pit-yellow)", bg: "rgba(255,196,0,0.10)" },
  manual:  { label: "Saisie manuelle",   color: "var(--ink-soft)",  bg: "rgba(255,255,255,0.04)" },
};

export default function Incomes() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | "new" | <income>

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/incomes");
      setData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du chargement.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const removeManual = async (income) => {
    const ok = await confirmAction(
      `Supprimer l'encaissement « ${income.label} » (${fmtEUR(income.amount_ttc)}) ?`
    );
    if (!ok) return;
    try {
      await api.delete(`/incomes/${income.id}`);
      toast.success("Encaissement supprimé.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la suppression.");
    }
  };

  const totals = data?.totals || { incomes_ttc: 0, expenses_ttc: 0, balance_ttc: 0, incomes_auto_ttc: 0, incomes_manual_ttc: 0 };
  const balancePositive = totals.balance_ttc >= 0;

  const rows = useMemo(() => data?.rows || [], [data]);

  return (
    <div data-testid="incomes-page" className="max-w-7xl mx-auto">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 animate-in">
        <div>
          <div className="flex items-center gap-2 font-body text-xs text-[#FFC400] font-bold mb-3">
            <ArrowDownToLine size={13} strokeWidth={2.5} /> Pilotage — Encaissements
          </div>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tight leading-[1.05]" style={{ color: "var(--chalk)" }}>
            Entrées
          </h1>
          <p className="text-[var(--ink-muted)] mt-3 font-body">
            Factures PitBoard <b>+</b> saisies manuelles. Balance trésorerie toujours visible.
          </p>
        </div>
        <button onClick={() => setModal("new")} className="btn btn-accent" data-testid="add-income">
          <Plus size={15} /> Saisie manuelle
        </button>
      </header>

      {/* Trio de KPI : Entrées · Dépenses · Balance */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8 animate-in delay-1">
        <KpiCard
          label="Entrées (TTC)"
          value={totals.incomes_ttc}
          sub={`${fmtEUR(totals.incomes_auto_ttc)} auto · ${fmtEUR(totals.incomes_manual_ttc)} manuel`}
          icon={Banknote}
          tone="primary"
          testid="kpi-incomes"
        />
        <KpiCard
          label="Dépenses (TTC)"
          value={totals.expenses_ttc}
          sub="Charges variables payées"
          icon={TrendingDown}
          tone="danger"
          testid="kpi-expenses"
        />
        <KpiCard
          label="Balance trésorerie"
          value={totals.balance_ttc}
          sub={balancePositive ? "Excédent" : "Déficit"}
          icon={Scale}
          tone={balancePositive ? "success" : "danger"}
          testid="kpi-balance"
          highlight
        />
      </div>

      {/* Tableau des encaissements */}
      <div className="card overflow-hidden p-0 animate-in delay-2">
        {loading ? (
          <div className="p-16 text-center" style={{ color: "var(--grey)" }}>
            <Loader2 size={20} className="animate-spin mx-auto mb-2" /> Chargement…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-16 text-center" style={{ color: "var(--grey)" }}>
            <Banknote size={36} className="mx-auto mb-3 opacity-40" strokeWidth={2} />
            <div className="font-display text-base mb-1" style={{ color: "var(--chalk)" }}>Aucun encaissement</div>
            <div className="text-[13px]">Validez une facture ou cliquez sur « Saisie manuelle » pour démarrer.</div>
          </div>
        ) : (
          <table className="table" data-testid="incomes-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Source</th>
                <th>Libellé</th>
                <th>Client</th>
                <th>Mode</th>
                <th className="text-right">Montant TTC</th>
                <th className="w-32 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = SOURCE_BADGE[r.source] || SOURCE_BADGE.manual;
                return (
                  <tr key={r.id} data-testid={`income-row-${r.id}`}>
                    <td className="font-mono text-xs">{r.date || "—"}</td>
                    <td>
                      <span
                        className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded-md inline-flex items-center gap-1"
                        style={{ color: badge.color, background: badge.bg }}
                      >
                        {r.source === "invoice" ? <FileText size={10} /> : <Pencil size={10} />}
                        {badge.label}
                      </span>
                    </td>
                    <td>
                      <div>{r.label}</div>
                      {r.external_reference && (
                        <div className="text-[10px]" style={{ color: "var(--grey)" }}>Réf : {r.external_reference}</div>
                      )}
                    </td>
                    <td>{r.client_text || "—"}</td>
                    <td className="text-[12px]">{METHOD_LABELS[r.payment_method] || r.payment_method || "—"}</td>
                    <td className="text-right font-semibold numerals">{fmtEUR(r.amount_ttc)}</td>
                    <td className="text-center">
                      {r.source === "manual" ? (
                        <div className="inline-flex gap-1">
                          <button onClick={() => setModal(r)} className="btn btn-ghost btn-sm" title="Modifier" data-testid={`edit-income-${r.id}`}>
                            <Pencil size={12} />
                          </button>
                          <button onClick={() => removeManual(r)} className="btn btn-ghost btn-sm" title="Supprimer" data-testid={`delete-income-${r.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      ) : (
                        <span className="text-[11px]" style={{ color: "var(--grey)" }}>Auto</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <IncomeModal
          item={modal === "new" ? null : modal}
          onClose={() => setModal(null)}
          onSaved={async () => { await load(); setModal(null); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, sub, icon: Icon, tone = "primary", testid, highlight = false }) {
  const colors = {
    primary: { fg: "var(--pit-yellow)", border: "var(--steel)" },
    danger:  { fg: "rgb(248,113,113)", border: "rgba(248,113,113,0.30)" },
    success: { fg: "rgb(34,197,94)",   border: "rgba(34,197,94,0.30)" },
  }[tone];
  return (
    <div
      className="card p-5"
      style={{
        borderColor: highlight ? colors.fg : colors.border,
        boxShadow: highlight ? `0 0 0 1px ${colors.fg} inset` : undefined,
      }}
      data-testid={testid}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: "var(--grey)" }}>
          {label}
        </div>
        <Icon size={18} style={{ color: colors.fg }} />
      </div>
      <div className="font-display text-3xl numerals" style={{ color: colors.fg, fontWeight: 700 }}>
        {fmtEUR(value)}
      </div>
      <div className="text-[11px] mt-1" style={{ color: "var(--grey)" }}>{sub}</div>
    </div>
  );
}

function IncomeModal({ item, onClose, onSaved }) {
  const [form, setForm] = useState(item ? {
    date: item.date,
    label: item.label,
    client_text: item.client_text || "",
    amount_ht: item.amount_ht ?? "",
    vat_rate: item.vat_rate ?? 20,
    payment_method: item.payment_method || "virement",
    external_reference: item.external_reference || "",
  } : {
    date: new Date().toISOString().slice(0, 10),
    label: "",
    client_text: "",
    amount_ht: "",
    vat_rate: 20,
    payment_method: "virement",
    external_reference: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.label.trim()) { toast.error("Libellé requis."); return; }
    const amt = Number(form.amount_ht);
    if (!Number.isFinite(amt) || amt <= 0) { toast.error("Montant HT > 0 requis."); return; }
    setSaving(true);
    try {
      const payload = {
        date: form.date,
        label: form.label.trim(),
        client_text: form.client_text.trim(),
        amount_ht: amt,
        vat_rate: Number(form.vat_rate) || 0,
        payment_method: form.payment_method,
        external_reference: form.external_reference.trim(),
      };
      if (item) {
        await api.patch(`/incomes/${item.id}`, payload);
        toast.success("Encaissement modifié.");
      } else {
        await api.post("/incomes", payload);
        toast.success("✅ Encaissement enregistré.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'enregistrement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" data-testid="income-modal">
      <div className="modal max-w-xl">
        <header className="flex items-center justify-between mb-2">
          <h3 className="font-display text-xl tracking-tight" style={{ color: "var(--chalk)" }}>
            {item ? "Modifier l'encaissement" : "Saisie manuelle d'un encaissement"}
          </h3>
          <button onClick={onClose} className="btn btn-ghost btn-sm">
            <X size={14} />
          </button>
        </header>

        <p className="text-[12.5px] mb-5" style={{ color: "var(--ink-soft)" }}>
          Pour les clients qui utilisent un autre logiciel de facturation (Henry…) et qui
          souhaitent quand même piloter leur trésorerie depuis PitBoard.
        </p>

        <div className="grid gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Date *</label>
              <input type="date" className="input mt-1" value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="inc-date" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Mode</label>
              <select className="input mt-1" value={form.payment_method}
                onChange={(e) => setForm({ ...form, payment_method: e.target.value })} data-testid="inc-method">
                <option value="virement">Virement</option>
                <option value="cb">CB</option>
                <option value="cheque">Chèque</option>
                <option value="especes">Espèces</option>
                <option value="autre">Autre</option>
              </select>
            </div>
          </div>

          <div>
            <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Libellé *</label>
            <input type="text" className="input mt-1" placeholder="Ex: Facture Dupont salle de bain"
              value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="inc-label" autoFocus />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Client</label>
              <input type="text" className="input mt-1" placeholder="Ex: M. Dupont"
                value={form.client_text} onChange={(e) => setForm({ ...form, client_text: e.target.value })} data-testid="inc-client" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Réf. externe</label>
              <input type="text" className="input mt-1" placeholder="Ex: Henry #2025-042"
                value={form.external_reference} onChange={(e) => setForm({ ...form, external_reference: e.target.value })} data-testid="inc-ref" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>Montant HT *</label>
              <input type="number" step="0.01" className="input mt-1 numerals" placeholder="Ex: 1200"
                value={form.amount_ht} onChange={(e) => setForm({ ...form, amount_ht: e.target.value })} data-testid="inc-amount" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest" style={{ color: "var(--grey)" }}>TVA</label>
              <select className="input mt-1" value={form.vat_rate}
                onChange={(e) => setForm({ ...form, vat_rate: Number(e.target.value) })} data-testid="inc-vat">
                <option value={0}>0 %</option>
                <option value={5.5}>5,5 %</option>
                <option value={10}>10 %</option>
                <option value={20}>20 %</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="btn btn-outline" disabled={saving}>Annuler</button>
          <button onClick={submit} className="btn btn-accent" disabled={saving} data-testid="inc-save">
            {saving ? <><Loader2 size={14} className="animate-spin" /> Enregistrement…</>
                    : <><Save size={14} /> Enregistrer</>}
          </button>
        </div>
      </div>
    </div>
  );
}
